import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CampaignCreativeType,
  CampaignPackage,
  CampaignPackageCreativePricing,
  CampaignPackageInput,
  getPackagePriceForCreative,
  getPackageStartingPrice,
  listActivePackagesForAdmin,
  createCampaignPackage,
  updateCampaignPackage,
  disableCampaignPackage,
} from '../../api/packages';

type CampaignPackageForm = CampaignPackageInput & {
  id?: string;
  creativePricing: CampaignPackageCreativePricing;
};

const CREATIVE_TYPES: CampaignCreativeType[] = ['text', 'image', 'video'];

const emptyCreativePricing: CampaignPackageCreativePricing = {
  text: undefined,
  image: undefined,
  video: undefined,
};

const emptyForm: CampaignPackageForm = {
  id: undefined,
  name: '',
  description: '',
  basePrice: 0,
  displayDurationSeconds: undefined,
  creativePricing: emptyCreativePricing,
  currency: 'USD',
  minAmount: undefined,
  maxAmount: undefined,
};

const formatCurrency = (value: number | undefined, currency = 'USD'): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'Not set';
  }

  return `$${value.toFixed(2)} ${currency}`;
};

const formatCreativeLabel = (creativeType: CampaignCreativeType): string =>
  creativeType === 'text' ? 'Text' : creativeType === 'image' ? 'Image' : 'Video';

export const CampaignPackagesAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState<CampaignPackage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);

  const loadPackages = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listActivePackagesForAdmin();
      setPackages(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load campaign packages';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPackages();
  }, []);

  const handleChange = (field: keyof CampaignPackageInput, value: string) => {
    setForm((prev) => {
      if (
        field === 'basePrice' ||
        field === 'minAmount' ||
        field === 'maxAmount' ||
        field === 'displayDurationSeconds'
      ) {
        const numeric = value === '' ? undefined : Number(value);
        return {
          ...prev,
          [field]: Number.isFinite(numeric as number) ? (numeric as number) : undefined,
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleCreativePriceChange = (creativeType: CampaignCreativeType, value: string) => {
    setForm((prev) => {
      const numeric = value === '' ? undefined : Number(value);
      return {
        ...prev,
        creativePricing: {
          ...prev.creativePricing,
          [creativeType]: Number.isFinite(numeric as number) ? (numeric as number) : undefined,
        },
      };
    });
  };

  const handleEdit = (pkg: CampaignPackage) => {
    setForm({
      id: pkg._id,
      name: pkg.name,
      description: pkg.description ?? '',
      basePrice: pkg.basePrice,
      displayDurationSeconds: pkg.displayDurationSeconds,
      creativePricing: {
        text: pkg.creativePricing?.text,
        image: pkg.creativePricing?.image,
        video: pkg.creativePricing?.video,
      },
      currency: pkg.currency || 'USD',
      minAmount: pkg.minAmount,
      maxAmount: pkg.maxAmount,
    });
  };

  const resetForm = () => setForm(emptyForm);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !Number.isFinite(form.basePrice) || form.basePrice <= 0) {
      setError('Please provide a package name and a valid base price.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const creativePricing = Object.fromEntries(
        CREATIVE_TYPES.map((creativeType) => [creativeType, form.creativePricing[creativeType]]),
      ) as CampaignPackageCreativePricing;
      const hasCreativePricing = CREATIVE_TYPES.some(
        (creativeType) => typeof creativePricing[creativeType] === 'number',
      );

      const payload: CampaignPackageInput = {
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        basePrice: form.basePrice,
        displayDurationSeconds: form.displayDurationSeconds,
        creativePricing: hasCreativePricing ? creativePricing : undefined,
        currency: form.currency || 'USD',
        minAmount: form.minAmount,
        maxAmount: form.maxAmount,
      };

      if (form.id) {
        await updateCampaignPackage(form.id, payload);
      } else {
        await createCampaignPackage(payload);
      }

      resetForm();
      await loadPackages();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save campaign package';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async (pkg: CampaignPackage) => {
    if (!window.confirm(`Disable package "${pkg.name}"?`)) return;
    setSaving(true);
    setError(null);
    try {
      await disableCampaignPackage(pkg._id);
      await loadPackages();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to disable campaign package';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Campaign packages</h2>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Configure the names and price ranges your advertisers see at checkout.
          </p>
        </div>
      </header>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <form
        onSubmit={handleSubmit}
        className="space-y-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3"
      >
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Package name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="block w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-900 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Weekend city loop"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
              Base / fallback price (USD)
            </label>
            <input
              type="number"
              min={0}
              step="1"
              value={Number.isFinite(form.basePrice) ? String(form.basePrice) : ''}
              onChange={(e) => handleChange('basePrice', e.target.value)}
              className="block w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-900 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Used when a text, image, or video-specific price is not set.
            </p>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
              Display duration (seconds)
            </label>
            <input
              type="number"
              min={1}
              step="1"
              value={
                typeof form.displayDurationSeconds === 'number'
                  ? String(form.displayDurationSeconds)
                  : ''
              }
              onChange={(e) => handleChange('displayDurationSeconds', e.target.value)}
              className="block w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-900 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="15"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
              Min amount (optional)
            </label>
            <input
              type="number"
              min={0}
              step="1"
              value={typeof form.minAmount === 'number' ? String(form.minAmount) : ''}
              onChange={(e) => handleChange('minAmount', e.target.value)}
              className="block w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-900 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
              Max amount (optional)
            </label>
            <input
              type="number"
              min={0}
              step="1"
              value={typeof form.maxAmount === 'number' ? String(form.maxAmount) : ''}
              onChange={(e) => handleChange('maxAmount', e.target.value)}
              className="block w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-900 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="space-y-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
          <div>
            <p className="text-xs font-medium text-slate-700 dark:text-slate-300">Creative pricing</p>
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              Set different prices for text, image, and video ads. Leave any field blank to use the
              base price instead.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            {CREATIVE_TYPES.map((creativeType) => (
              <div key={creativeType} className="space-y-1">
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                  {formatCreativeLabel(creativeType)} ad price (USD)
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={
                    typeof form.creativePricing[creativeType] === 'number'
                      ? String(form.creativePricing[creativeType])
                      : ''
                  }
                  onChange={(e) => handleCreativePriceChange(creativeType, e.target.value)}
                  className="block w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-900 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder={String(form.basePrice || 0)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Description (optional)</label>
          <textarea
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={2}
            className="block w-full resize-none rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-900 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="How and where this package will run."
          />
        </div>

        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={resetForm}
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50"
          >
            Clear
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {form.id ? 'Update package' : 'Add package'}
          </button>
        </div>
      </form>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-slate-50 dark:bg-slate-900 font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Duration</th>
              <th className="px-3 py-2">Pricing</th>
              <th className="px-3 py-2">Range</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-slate-500 dark:text-slate-400">
                  Loading packages...
                </td>
              </tr>
            )}
            {!loading && !packages.length && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-slate-500 dark:text-slate-400">
                  No active packages yet.
                </td>
              </tr>
            )}
            {!loading &&
              packages.map((pkg) => (
                <tr key={pkg._id}>
                  <td className="px-3 py-2 text-slate-900 dark:text-slate-100">
                    <div className="font-medium">{pkg.name}</div>
                    {pkg.description && (
                      <div className="mt-1 max-w-xs text-[11px] text-slate-500 dark:text-slate-400">{pkg.description}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                    {typeof pkg.displayDurationSeconds === 'number'
                      ? `${pkg.displayDurationSeconds} sec`
                      : 'Uses rotation default'}
                  </td>
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                    <div className="font-medium text-slate-900 dark:text-slate-100">
                      Starting at {formatCurrency(getPackageStartingPrice(pkg), pkg.currency || 'USD')}
                    </div>
                    <div className="mt-1 space-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                      {CREATIVE_TYPES.map((creativeType) => (
                        <div key={creativeType}>
                          {formatCreativeLabel(creativeType)}:{' '}
                          {formatCurrency(
                            getPackagePriceForCreative(pkg, creativeType),
                            pkg.currency || 'USD',
                          )}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                    {typeof pkg.minAmount === 'number' || typeof pkg.maxAmount === 'number'
                      ? `${typeof pkg.minAmount === 'number' ? `$${pkg.minAmount}` : 'Any'} – ${
                          typeof pkg.maxAmount === 'number' ? `$${pkg.maxAmount}` : 'Any'
                        }`
                      : 'Any budget'}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(pkg)}
                      className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-[11px] font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDisable(pkg)}
                      className="inline-flex items-center rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-100"
                    >
                      Disable
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        navigate('/admin', {
                          state: {
                            focus: 'campaign-payments',
                            packageId: pkg._id,
                            packageName: pkg.name,
                          },
                        })
                      }
                      className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-[11px] font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50"
                    >
                      View payments
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CampaignPackagesAdmin;
